import { useCallback, useState } from 'react';
import { renderBall, exportSizeFor, toUploadBlob } from './export';
import type { Photo } from './photos';
import { uploadAll } from './upload';
import { finalizeGame, initGame } from '../lib/api';

export interface SaveProgress {
  phase: 'idle' | 'preparing' | 'uploading' | 'finishing' | 'done' | 'error';
  /** 0 → 1 across the whole save, for a progress bar. */
  fraction: number;
  message?: string;
}

/**
 * Saves a set: reserve the link, push each ball image straight to storage, then
 * finalize.
 *
 * The three steps exist so a half-finished upload can never produce a broken game —
 * a game only becomes playable once every image has actually landed.
 */
export function useSaveGame() {
  const [progress, setProgress] = useState<SaveProgress>({ phase: 'idle', fraction: 0 });

  const save = useCallback(
    async (
      slug: string,
      title: string,
      photos: Photo[],
      passphrase: string,
    ): Promise<string | null> => {
      try {
        setProgress({ phase: 'preparing', fraction: 0.02, message: 'Reserving your link…' });
        const { token, uploads } = await initGame({
          slug,
          title,
          ballCount: photos.length,
          passphrase,
        });

        setProgress({ phase: 'preparing', fraction: 0.08, message: 'Preparing photos…' });
        const blobs = await Promise.all(
          photos.map(async (photo, index) => {
            const canvas = renderBall(photo, exportSizeFor(index + 1, photos.length));
            return toUploadBlob(canvas);
          }),
        );

        await uploadAll(
          uploads.map((slot, index) => ({ slot, blob: blobs[index]! })),
          (done, total) =>
            setProgress({
              phase: 'uploading',
              fraction: 0.1 + 0.8 * (done / total),
              message: `Uploading ${done} of ${total}…`,
            }),
        );

        setProgress({ phase: 'finishing', fraction: 0.95, message: 'Finishing up…' });
        const game = await finalizeGame(slug, token);

        setProgress({ phase: 'done', fraction: 1 });
        return game.slug;
      } catch (error) {
        setProgress({
          phase: 'error',
          fraction: 0,
          message: error instanceof Error ? error.message : 'Something went wrong saving.',
        });
        return null;
      }
    },
    [],
  );

  const reset = useCallback(() => setProgress({ phase: 'idle', fraction: 0 }), []);

  return { progress, save, reset };
}
